import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Card,
    DatePicker,
    Input,
    InputNumber,
    Select,
    Space,
    Switch,
    Typography
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { tmApi } from '../utils/taskManager';

const { TextArea } = Input;
const { Text } = Typography;

const toDayjs = (value, withTime) => {
    if (!value) return null;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
};

const SimpleValueInput = ({
    valueType,
    value,
    onChange,
    possibleValues = [],
    dictionaryId,
    dictionaryItems,
    isMultiple,
    placeholder,
    disabled,
    sourceOptions
}) => {
    if (valueType === 'boolean') {
        return (
            <Switch
                checked={Boolean(value)}
                disabled={disabled}
                onChange={onChange}
            />
        );
    }

    if (valueType === 'number') {
        return (
            <InputNumber
                style={{ width: '100%' }}
                value={value}
                disabled={disabled}
                placeholder={placeholder}
                onChange={onChange}
            />
        );
    }

    if (valueType === 'date' || valueType === 'datetime') {
        const withTime = valueType === 'datetime';
        return (
            <DatePicker
                style={{ width: '100%' }}
                showTime={withTime}
                value={toDayjs(value, withTime)}
                disabled={disabled}
                onChange={date =>
                    onChange(
                        date
                            ? withTime
                                ? date.toISOString()
                                : date.format('YYYY-MM-DD')
                            : null
                    )
                }
            />
        );
    }

    if (valueType === 'text') {
        return (
            <TextArea
                rows={2}
                value={value ?? ''}
                disabled={disabled}
                placeholder={placeholder}
                onChange={e => onChange(e.target.value)}
            />
        );
    }

    if (valueType === 'select' || sourceOptions) {
        const options = (sourceOptions || possibleValues || []).map(
            item =>
                typeof item === 'object'
                    ? {
                          value: item.value,
                          label: item.label || item.value
                      }
                    : { value: item, label: item }
        );

        return (
            <Select
                style={{ width: '100%' }}
                allowClear
                showSearch
                optionFilterProp="label"
                mode={isMultiple ? 'multiple' : undefined}
                options={options}
                value={
                    isMultiple
                        ? Array.isArray(value)
                            ? value
                            : value
                              ? [value]
                              : []
                        : value ?? undefined
                }
                disabled={disabled}
                placeholder={placeholder || 'Выберите'}
                onChange={onChange}
            />
        );
    }

    if (valueType === 'dictionary') {
        return (
            <AsyncDictionarySelect
                dictionaryId={dictionaryId}
                seedItems={dictionaryItems}
                value={value}
                onChange={onChange}
                isMultiple={isMultiple}
                disabled={disabled}
                placeholder={placeholder}
            />
        );
    }

    return (
        <Input
            value={value ?? ''}
            disabled={disabled}
            placeholder={placeholder}
            onChange={e => onChange(e.target.value)}
        />
    );
};

const AsyncDictionarySelect = ({
    dictionaryId,
    seedItems,
    value,
    onChange,
    isMultiple,
    disabled,
    placeholder
}) => {
    const [options, setOptions] = useState(() =>
        (seedItems || []).map(item => ({
            value: item.value,
            label: item.label || item.value
        }))
    );
    const [fetching, setFetching] = useState(false);
    const timerRef = useRef(null);
    const valueKey = Array.isArray(value)
        ? value.join(',')
        : String(value || '');

    const mergeOptions = list => {
        setOptions(prev => {
            const map = new Map();
            [...prev, ...(list || [])].forEach(opt => {
                if (opt?.value == null || opt.value === '') return;
                map.set(String(opt.value), {
                    value: opt.value,
                    label: opt.label || opt.value
                });
            });
            return Array.from(map.values());
        });
    };

    const fetchItems = async (q = '') => {
        if (!dictionaryId) return;
        setFetching(true);
        try {
            const params = { limit: 80, slim: 1 };
            if (q) params.q = q;
            const selected = Array.isArray(value)
                ? value
                : value
                  ? [value]
                  : [];
            if (selected.length) {
                params.values = selected.join(',');
            }
            const { data } = await tmApi.get(
                `/api/task-manager/dictionaries/${dictionaryId}/items`,
                { params }
            );
            mergeOptions(
                (data.data || []).map(item => ({
                    value: item.value,
                    label: item.label || item.value
                }))
            );
        } catch (_) {
            /* keep previous options */
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        if (seedItems?.length) {
            mergeOptions(
                seedItems.map(item => ({
                    value: item.value,
                    label: item.label || item.value
                }))
            );
        }
        fetchItems('');
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dictionaryId, valueKey]);

    const onSearch = text => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            fetchItems(String(text || '').trim());
        }, 250);
    };

    return (
        <Select
            style={{ width: '100%' }}
            allowClear
            showSearch
            filterOption={false}
            onSearch={onSearch}
            notFoundContent={fetching ? 'Поиск…' : 'Нет данных'}
            mode={isMultiple ? 'multiple' : undefined}
            options={options}
            value={
                isMultiple
                    ? Array.isArray(value)
                        ? value
                        : value
                          ? [value]
                          : []
                    : value ?? undefined
            }
            disabled={disabled || !dictionaryId}
            placeholder={
                dictionaryId
                    ? placeholder || 'Поиск по справочнику'
                    : 'Справочник не выбран'
            }
            onChange={onChange}
        />
    );
};

const CompositeEditor = ({
    subFields,
    value,
    onChange,
    dictionariesMap,
    sourceMaps,
    disabled
}) => {
    const objectValue =
        value && typeof value === 'object' && !Array.isArray(value)
            ? value
            : {};

    return (
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {(subFields || []).map(sub => (
                <div key={sub.key}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {sub.label || sub.key}
                        {sub.required ? ' *' : ''}
                    </Text>
                    <SimpleValueInput
                        valueType={sub.value_type || 'string'}
                        value={objectValue[sub.key]}
                        possibleValues={sub.possible_values}
                        dictionaryId={sub.dictionary_id}
                        dictionaryItems={
                            dictionariesMap?.[sub.dictionary_id]
                                ?.items || []
                        }
                        sourceOptions={
                            sub.source
                                ? sourceMaps?.[sub.source] || []
                                : null
                        }
                        isMultiple={Boolean(sub.is_multiple)}
                        disabled={disabled}
                        onChange={next =>
                            onChange({
                                ...objectValue,
                                [sub.key]: next
                            })
                        }
                    />
                </div>
            ))}
        </Space>
    );
};

const RepeaterEditor = ({
    subFields,
    value,
    onChange,
    dictionariesMap,
    sourceMaps,
    disabled,
    itemLabel = 'Элемент',
    minItems = 0,
    maxItems
}) => {
    const list = Array.isArray(value) ? value : [];

    const addItem = () => {
        if (maxItems != null && list.length >= maxItems) return;
        const blank = {};
        (subFields || []).forEach(sub => {
            blank[sub.key] = sub.is_multiple ? [] : null;
        });
        onChange([...list, blank]);
    };

    const removeItem = index => {
        if (list.length <= minItems) return;
        onChange(list.filter((_, i) => i !== index));
    };

    const patchItem = (index, next) => {
        const copy = [...list];
        copy[index] = next;
        onChange(copy);
    };

    return (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {list.map((item, index) => (
                <Card
                    key={index}
                    size="small"
                    title={`${itemLabel} ${index + 1}`}
                    extra={
                        !disabled ? (
                            <Button
                                type="text"
                                danger
                                size="small"
                                icon={<MinusCircleOutlined />}
                                disabled={list.length <= minItems}
                                onClick={() => removeItem(index)}
                            />
                        ) : null
                    }
                >
                    <CompositeEditor
                        subFields={subFields}
                        value={item}
                        dictionariesMap={dictionariesMap}
                        sourceMaps={sourceMaps}
                        disabled={disabled}
                        onChange={next => patchItem(index, next)}
                    />
                </Card>
            ))}
            {!disabled ? (
                <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    disabled={
                        maxItems != null && list.length >= maxItems
                    }
                    onClick={addItem}
                >
                    Добавить {itemLabel.toLowerCase()}
                </Button>
            ) : null}
        </Space>
    );
};

/**
 * Универсальный контрол значения кастомного поля.
 * Работает и как controlled (value/onChange), и через Form.
 */
const TmFieldValueInput = ({
    field,
    value,
    onChange,
    dictionariesMap: externalMap,
    disabled
}) => {
    const [localMap, setLocalMap] = useState({});
    const [sourceMaps, setSourceMaps] = useState({});

    const dictionaryIds = useMemo(() => {
        const ids = new Set();
        if (field?.dictionary_id) {
            ids.add(Number(field.dictionary_id));
        }
        const subs = field?.config?.sub_fields || [];
        subs.forEach(sub => {
            if (sub.dictionary_id) {
                ids.add(Number(sub.dictionary_id));
            }
        });
        return Array.from(ids);
    }, [field]);

    const sourceKeys = useMemo(() => {
        const keys = new Set();
        const cfg = field?.config || {};
        if (cfg.source) keys.add(cfg.source);
        (cfg.sub_fields || []).forEach(sub => {
            if (sub.source) keys.add(sub.source);
        });
        return Array.from(keys);
    }, [field]);

    const dictionaryIdsKey = dictionaryIds.join(',');
    const sourceKeysKey = sourceKeys.join(',');

    useEffect(() => {
        if (externalMap) return;
        if (!dictionaryIds.length) return;

        let cancelled = false;

        (async () => {
            const map = {};
            await Promise.all(
                dictionaryIds.map(async id => {
                    try {
                        const { data } = await tmApi.get(
                            `/api/task-manager/dictionaries/${id}/items`,
                            {
                                params: {
                                    limit: 100,
                                    slim: 1
                                }
                            }
                        );
                        map[id] = {
                            ...(data.dictionary || { id }),
                            items: (data.data || []).filter(
                                item => item.is_active !== false
                            ),
                            truncated: Boolean(data.truncated),
                            total: data.total
                        };
                    } catch (_) {
                        map[id] = { id, items: [] };
                    }
                })
            );
            if (!cancelled) setLocalMap(map);
        })();

        return () => {
            cancelled = true;
        };
    }, [dictionaryIdsKey, externalMap]);

    useEffect(() => {
        if (!sourceKeys.length) return;

        let cancelled = false;

        (async () => {
            const map = {};
            await Promise.all(
                sourceKeys.map(async key => {
                    const url =
                        key === 'tmc'
                            ? '/api/task-manager/lookups/tmc-items'
                            : key === 'installers'
                              ? '/api/task-manager/lookups/installers'
                              : null;
                    if (!url) {
                        map[key] = [];
                        return;
                    }
                    try {
                        const { data } = await tmApi.get(url);
                        map[key] = data.data || [];
                    } catch (_) {
                        map[key] = [];
                    }
                })
            );
            if (!cancelled) setSourceMaps(map);
        })();

        return () => {
            cancelled = true;
        };
    }, [sourceKeysKey]);

    const dictionariesMap = externalMap || localMap;
    const config = field?.config || {};
    const valueType = field?.value_type || 'string';

    if (valueType === 'composite') {
        return (
            <CompositeEditor
                subFields={config.sub_fields || []}
                value={value}
                onChange={onChange}
                dictionariesMap={dictionariesMap}
                sourceMaps={sourceMaps}
                disabled={disabled}
            />
        );
    }

    if (valueType === 'repeater') {
        return (
            <RepeaterEditor
                subFields={config.sub_fields || []}
                value={value}
                onChange={onChange}
                dictionariesMap={dictionariesMap}
                sourceMaps={sourceMaps}
                disabled={disabled}
                itemLabel={config.item_label || 'Элемент'}
                minItems={config.min_items || 0}
                maxItems={config.max_items}
            />
        );
    }

    return (
        <SimpleValueInput
            valueType={valueType}
            value={value}
            onChange={onChange}
            possibleValues={field?.possible_values}
            dictionaryId={field?.dictionary_id}
            dictionaryItems={
                dictionariesMap?.[field?.dictionary_id]?.items || []
            }
            sourceOptions={
                config.source ? sourceMaps[config.source] || [] : null
            }
            isMultiple={Boolean(field?.is_multiple)}
            placeholder={config.placeholder}
            disabled={disabled}
        />
    );
};

export default TmFieldValueInput;
